/**
 * Role Has Users Dialog
 *
 * Informational dialog that prevents role deletion when users are assigned.
 * Displays instructions to reassign users before deletion.
 *
 * @module domains/personnel/presentation/components/shared/RoleHasUsersDialog
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@parrot-software/design-system';

/**
 * Props for RoleHasUsersDialog component
 */
export interface RoleHasUsersDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Number of users assigned to the role */
  usersCount: number;
}

/**
 * Role Has Users Informational Dialog Component
 *
 * Displays a blocking dialog when attempting to delete a role with assigned users.
 * Provides instructions to reassign users before deletion.
 *
 * @param {RoleHasUsersDialogProps} props - Component properties
 * @returns {React.ReactElement} Informational dialog
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <RoleHasUsersDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   usersCount={5}
 * />
 */
export const RoleHasUsersDialog = ({ open, onOpenChange, usersCount }: RoleHasUsersDialogProps): React.ReactElement => {
  const { t } = useTranslation('roles');

  const handleAccept = () => {
    onOpenChange(false);
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[451px] font-sans">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('cannotDelete.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p className="text-muted-foreground">{t('cannotDelete.description')}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleAccept}>{t('cannotDelete.acceptButton')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
